import React, { useState, useEffect, useRef } from 'react';
import { CRMIcon } from '../ui';
import axiosInstance from '../../utils/axiosConfig';
import { useUserInfo } from '../../contexts/UserContext';
import { useFlashMessage } from '../FlashMessage';
import { dashboardService } from '../../services/dashboardService';
import { crmService } from '../../services/crmService';
import CommodityUploadModal from './CommodityUploadModal';
import ConfirmationPopup from '../ConfirmationPopup';
import { getErrorMessage } from '../../utils/errorMessages';
import '../CRMProfileScreen.css';

const CRMCommodityTradingTab = ({ profileData, onDataChange, isNewProfile }) => {
    const { userInfo } = useUserInfo();
    const { showError, showSuccess } = useFlashMessage();
    
    const [formData, setFormData] = useState({
        traded_commodities: [],
        known_trade_partners: [],
        certificate_files: [],
        trading_stats: {
            historical_trade_volume: {},
            turnover_annual: {},
            trade_frequency: '',
            last_transaction_date: '',
            average_deal_size: { volume: '', value: '' },
            project_links: ''
        },
        ...profileData
    });

    // COA Document management state
    const [coaDocuments, setCoaDocuments] = useState([]);
    const [loadingCoaDocuments, setLoadingCoaDocuments] = useState(false);
    const [coaDocumentError, setCoaDocumentError] = useState(null);
    const [showCoaDocuments, setShowCoaDocuments] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadingCoa, setUploadingCoa] = useState(false);
    const [groupedCoaDocuments, setGroupedCoaDocuments] = useState({});
    
    // Delete confirmation state
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    
    // Duplicate detection state
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateFileInfo, setDuplicateFileInfo] = useState(null);
    
    // Current user info for employee attribution
    const [currentUserInfo, setCurrentUserInfo] = useState(null);
    
    // Employee information state for document metadata
    const [employeeInfoCache, setEmployeeInfoCache] = useState({}); // Cache for employee info by user_id
    const [allEmployees, setAllEmployees] = useState([]); // Personio employee list for lookup

    // Known Trade Partners - Modern multi-select state management
    const [knownPartnersOptions, setKnownPartnersOptions] = useState([]);
    const [filteredKnownPartnersOptions, setFilteredKnownPartnersOptions] = useState([]);
    const [loadingKnownPartners, setLoadingKnownPartners] = useState(false);
    const [showKnownPartnersDropdown, setShowKnownPartnersDropdown] = useState(false);
    const knownPartnersRef = useRef(null);

    // Search functionality for known trade partners dropdown
    const [knownPartnersSearchTerm, setKnownPartnersSearchTerm] = useState('');
    const [debouncedKnownPartnersSearchTerm, setDebouncedKnownPartnersSearchTerm] = useState('');
    const knownPartnersSearchInputRef = useRef(null);

    // Handle extracted commodities from document analysis
    useEffect(() => {
        if (profileData?.extracted_commodities && Array.isArray(profileData.extracted_commodities)) {
            console.log('📦 CRMCommodityTradingTab: Extracted commodities detected:', profileData.extracted_commodities);
            
            // CRM traded_commodities is just a text field (commodity_name), no need to match to product types
            const newCommodities = [];
            
            profileData.extracted_commodities.forEach(commodityName => {
                // Check if not already in the list
                const isDuplicate = (formData.traded_commodities || []).some(tc => 
                    tc.commodity_name?.toLowerCase() === commodityName.toLowerCase()
                );
                
                if (!isDuplicate) {
                    newCommodities.push({
                        commodity_name: commodityName,
                        commodity_specifications: 'Extracted from document analysis'
                    });
                }
            });
            
            if (newCommodities.length > 0) {
                console.log('📦 CRMCommodityTradingTab: Adding extracted commodities:', newCommodities);
                
                // Add new commodities to traded_commodities
                const updatedCommodities = [
                    ...(formData.traded_commodities || []),
                    ...newCommodities
                ];
                
                setFormData(prev => ({
                    ...prev,
                    traded_commodities: updatedCommodities,
                    extracted_commodities: undefined // Clear after processing
                }));
                
                // Update parent component
                onDataChange('traded_commodities', updatedCommodities);
                
                showSuccess(`${newCommodities.length} commodity(ies) added from document analysis`, 4000);
            } else {
                console.log('📦 CRMCommodityTradingTab: All extracted commodities already exist');
            }
        }
    }, [profileData?.extracted_commodities]);

    // Handle outside clicks for dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (knownPartnersRef.current && !knownPartnersRef.current.contains(event.target)) {
                setShowKnownPartnersDropdown(false);
                // Clear search when closing dropdown
                setKnownPartnersSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Debounce search term for known trade partners
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedKnownPartnersSearchTerm(knownPartnersSearchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [knownPartnersSearchTerm]);

    // Filter known trade partners based on search term
    useEffect(() => {
        if (!debouncedKnownPartnersSearchTerm.trim()) {
            // If no search term, show no options initially (as requested)
            setFilteredKnownPartnersOptions([]);
        } else {
            // Filter options based on search term
            const filtered = knownPartnersOptions.filter(option =>
                option.toLowerCase().includes(debouncedKnownPartnersSearchTerm.toLowerCase())
            );
            setFilteredKnownPartnersOptions(filtered);
        }
    }, [debouncedKnownPartnersSearchTerm, knownPartnersOptions]);

    // Fetch partner names for known trade partners dropdown
    useEffect(() => {
        const fetchPartnerNames = async () => {
            try {
                setLoadingKnownPartners(true);
                const partnerNames = await crmService.getPartnerNames();
                setKnownPartnersOptions(partnerNames);
                console.log(`CRMCommodityTradingTab: Loaded ${partnerNames.length} partner names for known trade partners dropdown`);
            } catch (error) {
                console.error('CRMCommodityTradingTab: Error fetching partner names for known trade partners:', error);
                setKnownPartnersOptions([]); // Fallback to empty array
            } finally {
                setLoadingKnownPartners(false);
            }
        };

        fetchPartnerNames();
    }, []);

    useEffect(() => {
        if (profileData) {
            console.log('🔄 CommodityTradingTab: profileData updated, refreshing formData');
            
            // Transform known_trade_partners from objects to strings if needed
            let processedKnownPartners = [];
            if (profileData.known_trade_partners && Array.isArray(profileData.known_trade_partners)) {
                processedKnownPartners = profileData.known_trade_partners.map(partner => {
                    // If it's an object with trade_partner_name or partner_name, extract the name
                    if (typeof partner === 'object' && partner !== null) {
                        return partner.trade_partner_name || partner.partner_name || '';
                    }
                    // If it's already a string, use it as is
                    return partner;
                }).filter(name => name && name.trim() !== '');
            }
            
            setFormData({
                traded_commodities: [],
                known_trade_partners: [],
                certificate_files: [],
                trading_stats: {
                    historical_trade_volume: {},
                    turnover_annual: {},
                    trade_frequency: '',
                    last_transaction_date: '',
                    average_deal_size: { volume: '', value: '' },
                    project_links: ''
                },
                ...profileData,
                known_trade_partners: processedKnownPartners
            });
        }
    }, [profileData]);

    const handleInputChange = (field, value) => {
        const updatedData = {
            ...formData,
            [field]: value
        };
        setFormData(updatedData);
        onDataChange(field, updatedData[field]);
    };

    // Multi-select helper functions for known trade partners
    const handleKnownPartnersToggle = (partnerName) => {
        const currentSelections = formData.known_trade_partners || [];
        const updatedSelections = currentSelections.includes(partnerName)
            ? currentSelections.filter(item => item !== partnerName)
            : [...currentSelections, partnerName];
        
        handleInputChange('known_trade_partners', updatedSelections);
    };

    const removeKnownPartnerTag = (tagToRemove) => {
        const currentSelections = formData.known_trade_partners || [];
        const updatedSelections = currentSelections.filter(item => item !== tagToRemove);
        handleInputChange('known_trade_partners', updatedSelections);
    };

    // Traded Commodities Management
    const addTradedCommodity = () => {
        const currentCommodities = formData.traded_commodities || [];
        const newCommodity = {
            id: Date.now(),
            commodity_name: '',
            specifications: ''
        };
        handleInputChange('traded_commodities', [...currentCommodities, newCommodity]);
    };

    const removeTradedCommodity = (commodityId) => {
        const currentCommodities = formData.traded_commodities || [];
        const updatedCommodities = currentCommodities.filter(commodity => commodity.id !== commodityId);
        handleInputChange('traded_commodities', updatedCommodities);
    };

    const handleCommodityChange = (commodityId, field, value) => {
        const currentCommodities = formData.traded_commodities || [];
        const updatedCommodities = currentCommodities.map(commodity => 
            commodity.id === commodityId ? { ...commodity, [field]: value } : commodity
        );
        handleInputChange('traded_commodities', updatedCommodities);
    };



    // File Upload Management
    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);
        const currentFiles = formData.certificate_files || [];
        
        const newFiles = files.map(file => ({
            id: Date.now() + Math.random(),
            name: file.name,
            size: file.size,
            type: file.type,
            uploadDate: new Date().toISOString(),
            file: file // In real implementation, this would be uploaded to server
        }));
        
        handleInputChange('certificate_files', [...currentFiles, ...newFiles]);
    };

    const removeFile = (fileId) => {
        const currentFiles = formData.certificate_files || [];
        const updatedFiles = currentFiles.filter(file => file.id !== fileId);
        handleInputChange('certificate_files', updatedFiles);
    };

    const downloadFile = (file) => {
        // Placeholder for download functionality
        console.log('Download file:', file.name);
        alert(`Download functionality will be implemented with DocuWare integration. File: ${file.name}`);
    };



    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Get current user information for employee_id - same pattern as banking tab
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const userInfoData = await dashboardService.getUserInfo();
                setCurrentUserInfo(userInfoData);
            } catch (error) {
                console.error('Error fetching user info:', error);
                // Fallback to userInfo from context
                setCurrentUserInfo(userInfo);
            }
        };

        if (!currentUserInfo && userInfo?.user_id) {
            setCurrentUserInfo(userInfo);
        } else if (!currentUserInfo) {
            fetchUserInfo();
        }
    }, [userInfo, currentUserInfo]);

    // Fetch all employees data for employee lookup on document metadata
    useEffect(() => {
        const fetchAllEmployees = async () => {
            try {
                console.log('CRMCommodityTradingTab: Fetching all employees data for document metadata (HMS API)');
                
                // Use HMS-native employees list API
                const response = await axiosInstance.get('/api/employees/hms-employees/');
                
                if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                    setAllEmployees(response.data);
                    console.log('CRMCommodityTradingTab: Loaded employee data from HMS API:', response.data.length, 'employees');
                } else {
                    console.warn('CRMCommodityTradingTab: No employee data received from HMS API');
                }
            } catch (error) {
                console.error('CRMCommodityTradingTab: Error fetching employee data from HMS API:', error);
                // Fallback: try to use empty array to prevent crashes
                setAllEmployees([]);
            }
        };

        fetchAllEmployees();
    }, []); // Run once on component mount
    
    // Function to get employee info by Django user ID
    const getEmployeeInfoByUserId = async (djangoUserId) => {
        if (!djangoUserId) return null;
        
        // Check cache first
        if (employeeInfoCache[djangoUserId]) {
            console.log(`CRMCommodityTradingTab: Using cached employee info for user ID ${djangoUserId}`);
            return employeeInfoCache[djangoUserId];
        }
        
        try {
            console.log(`CRMCommodityTradingTab: Looking up employee info for Django user ID: ${djangoUserId}`);
            
            // For now, we'll use the Dashboard API to get user info by the current user
            // In a future enhancement, we could create a backend endpoint to lookup any user by ID
            // But for documents uploaded by the current user, we can use their own info
            if (currentUserInfo && currentUserInfo.user_id === parseInt(djangoUserId)) {
                const employeeInfo = {
                    first_name: currentUserInfo.first_name,
                    last_name: currentUserInfo.last_name,
                    position: currentUserInfo.position,
                    department: currentUserInfo.department,
                    office: currentUserInfo.office,
                    employee_id: currentUserInfo.employee_id
                };
                
                // Cache the result
                setEmployeeInfoCache(prev => ({
                    ...prev,
                    [djangoUserId]: employeeInfo
                }));
                
                console.log(`CRMCommodityTradingTab: Found employee info for user ${djangoUserId}:`, employeeInfo);
                return employeeInfo;
            } else {
                // For other users, we'd need a backend endpoint to fetch user info by ID
                // For now, return basic info indicating we can't fetch other users' data
                const placeholderInfo = {
                    first_name: 'User',
                    last_name: `ID ${djangoUserId}`,
                    position: 'Unknown',
                    department: 'Unknown',
                    office: 'Unknown',
                    employee_id: djangoUserId
                };
                
                console.log(`CRMCommodityTradingTab: Cannot fetch info for other user ID ${djangoUserId}, using placeholder`);
                return placeholderInfo;
            }
        } catch (error) {
            console.error(`CRMCommodityTradingTab: Error fetching employee info for user ID ${djangoUserId}:`, error);
            return null;
        }
    };

    // Auto-fetch COA documents when tab opens and profile is available
    useEffect(() => {
        if (profileData?.id) {
            console.log('Auto-fetching COA documents for partner:', profileData.id);
            fetchCoaDocuments();
            setShowCoaDocuments(true);
        }
    }, [profileData?.id]);

    // Fetch COA documents from DocuWare
    const fetchCoaDocuments = async () => {
        if (!profileData?.id) return;
        
        setLoadingCoaDocuments(true);
        setCoaDocumentError(null);
        
        try {
            console.log('Fetching COA documents for partner ID:', profileData.id);
            
            const response = await axiosInstance.get('/api/docuware/coa-certificates/documents/', {
                params: {
                    partner_id: profileData.id,
                    count: 100
                }
            });
            
            const documents = response.data.Items || [];
            setCoaDocuments(documents);
            
            // Group documents by commodity type
            const grouped = groupDocumentsByCommodity(documents);
            setGroupedCoaDocuments(grouped);
            
            console.log('Successfully fetched', documents.length, 'COA documents');
            
        } catch (error) {
            console.error('Error fetching COA documents:', error);
            setCoaDocumentError(error.response?.data?.error || 'Failed to load COA documents');
        } finally {
            setLoadingCoaDocuments(false);
        }
    };

    // Group documents by commodity type
    const groupDocumentsByCommodity = (documents) => {
        const grouped = {};
        
        documents.forEach(doc => {
            const fields = doc.Fields || [];
            const commodityField = fields.find(f => f.FieldName === 'COMMODITY_TYPE');
            const commodityType = commodityField?.Item || 'Unknown';
            
            if (!grouped[commodityType]) {
                grouped[commodityType] = [];
            }
            grouped[commodityType].push(doc);
        });
        
        return grouped;
    };

    // Duplicate detection helper functions
    const checkForDuplicateFilename = (filename) => {
        console.log('🔍 FRONTEND: Checking for duplicate filename:', filename);
        console.log('🔍 FRONTEND: Current documents:', coaDocuments);
        
        if (!coaDocuments || coaDocuments.length === 0) {
            console.log('✅ FRONTEND: No existing documents, no duplicates found');
            return { isDuplicate: false, existingDocument: null };
        }
        
        // Check if any existing document has the same filename in SUBJECT field
        const duplicateDocument = coaDocuments.find(doc => {
            const docInfo = getCoaDocumentInfo(doc);
            const isDup = docInfo.filename === filename;
            console.log(`🔍 FRONTEND: Comparing "${docInfo.filename}" with "${filename}": ${isDup}`);
            return isDup;
        });
        
        if (duplicateDocument) {
            console.log('⚠️ FRONTEND: Duplicate found:', duplicateDocument);
            return { isDuplicate: true, existingDocument: duplicateDocument };
        }
        
        console.log('✅ FRONTEND: No duplicates found');
        return { isDuplicate: false, existingDocument: null };
    };

    const generateIncrementedFilename = (originalFilename) => {
        console.log('🔢 FRONTEND: Generating incremented filename for:', originalFilename);
        
        if (!coaDocuments || coaDocuments.length === 0) {
            console.log('✅ FRONTEND: No existing documents, returning original filename');
            return originalFilename;
        }
        
        // Extract existing filenames from document list
        const existingFilenames = coaDocuments.map(doc => {
            const docInfo = getCoaDocumentInfo(doc);
            return docInfo.filename;
        }).filter(Boolean); // Remove any null/undefined values
        
        console.log('📋 FRONTEND: Existing filenames:', existingFilenames);
        
        // Parse the original filename to extract name and extension
        const lastDotIndex = originalFilename.lastIndexOf('.');
        const nameWithoutExt = lastDotIndex !== -1 ? originalFilename.substring(0, lastDotIndex) : originalFilename;
        const extension = lastDotIndex !== -1 ? originalFilename.substring(lastDotIndex) : '';
        
        console.log('📝 FRONTEND: Name without extension:', nameWithoutExt);
        console.log('📝 FRONTEND: Extension:', extension);
        
        // Start checking from (1) and increment until we find a unique name
        let counter = 1;
        let newFilename = `${nameWithoutExt} (${counter})${extension}`;
        
        while (existingFilenames.includes(newFilename)) {
            counter++;
            newFilename = `${nameWithoutExt} (${counter})${extension}`;
            console.log(`🔢 FRONTEND: Trying counter ${counter}: ${newFilename}`);
        }
        
        console.log('✅ FRONTEND: Generated unique filename:', newFilename);
        return newFilename;
    };

    // Handle COA file upload with commodity selection
    const handleCoaFileUpload = async (commodityType, file) => {
        // Validation
        if (!profileData?.id) {
            showError('Please save the partner profile first before uploading documents.');
            return;
        }
        
        if (!commodityType) {
            showError('Please select a commodity type.');
            return;
        }
        
        // File size validation (30MB)
        const maxSizeInBytes = 30 * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
            showError(`File size exceeds 30MB limit.`);
            return;
        }
        
        console.log('📤 FRONTEND: Starting COA file upload process');
        console.log('📄 FRONTEND: File details:', { name: file.name, size: file.size, type: file.type });
        
        // Check for duplicate filename before proceeding with upload
        const duplicateCheck = checkForDuplicateFilename(file.name);
        
        if (duplicateCheck.isDuplicate) {
            console.log('⚠️ FRONTEND: Duplicate detected, showing modal');
            // Store the upload details for later processing
            setDuplicateFileInfo({
                commodityType,
                file,
                existingDocument: duplicateCheck.existingDocument
            });
            setShowDuplicateModal(true);
            return; // Stop here and wait for user decision
        }
        
        // No duplicate found, proceed with normal upload
        console.log('✅ FRONTEND: No duplicate found, proceeding with upload');
        await performCoaFileUpload(commodityType, file, file.name);
    };

    // Separate function to handle the actual upload process
    const performCoaFileUpload = async (commodityType, file, finalFilename) => {
        console.log('🚀 FRONTEND: Performing COA file upload with filename:', finalFilename);
        
        setUploadingCoa(true);
        
        try {
            const employeeId = currentUserInfo?.user_id || '1';
            const partnerName = profileData?.legal_entity_name || 'Unknown Partner';
            const subject = finalFilename; // Use the final filename (potentially renamed) as subject
            
            console.log('📋 FRONTEND: Upload metadata:', {
                employeeId,
                partnerName,
                subject,
                commodityType
            });
            
            // Step 1: Create document record
            const recordData = {
                partner_name: partnerName,
                employee_id: employeeId,
                partner_id: profileData?.id.toString(),
                subject: subject,
                commodity_type: commodityType
            };
            
            console.log('📝 FRONTEND: Creating document record...');
            const recordResponse = await axiosInstance.post('/api/docuware/coa-certificates/create-record/', recordData);
            
            if (!recordResponse.data.success || !recordResponse.data.document_id) {
                throw new Error('Failed to create document record');
            }
            
            const documentId = recordResponse.data.document_id;
            console.log('✅ FRONTEND: Document record created with ID:', documentId);
            
            // Step 2: Upload file
            console.log('📤 FRONTEND: Uploading file content...');
            const fileFormData = new FormData();
            fileFormData.append('file', file);
            
            await axiosInstance.post(`/api/docuware/coa-certificates/upload-file/${documentId}/`, fileFormData);
            
            console.log('🎉 FRONTEND: COA file upload completed successfully');
            showSuccess(`COA certificate "${finalFilename}" uploaded successfully for ${commodityType}!`);
            
            // Refresh document list
            if (showCoaDocuments) {
                console.log('🔄 FRONTEND: Refreshing document list...');
                setTimeout(() => {
                    fetchCoaDocuments();
                }, 1000);
            }
            
        } catch (error) {
            console.error('❌ FRONTEND: COA upload error:', error);
            showError(getErrorMessage(error, 'Commodity operation failed. Please try again.'));
        } finally {
            setUploadingCoa(false);
        }
    };

    // Duplicate modal handlers
    const handleDuplicateKeepBoth = async () => {
        console.log('👥 FRONTEND: User chose to keep both files');
        
        if (!duplicateFileInfo) {
            console.error('❌ FRONTEND: No duplicate file info available');
            return;
        }
        
        const { commodityType, file } = duplicateFileInfo;
        const incrementedFilename = generateIncrementedFilename(file.name);
        
        console.log('📝 FRONTEND: Renaming file to:', incrementedFilename);
        
        // Close modal and proceed with upload using incremented filename
        setShowDuplicateModal(false);
        setDuplicateFileInfo(null);
        
        await performCoaFileUpload(commodityType, file, incrementedFilename);
    };

    const handleDuplicateReplace = async () => {
        console.log('🔄 FRONTEND: User chose to replace existing file');
        
        if (!duplicateFileInfo) {
            console.error('❌ FRONTEND: No duplicate file info available');
            return;
        }
        
        const { commodityType, file, existingDocument } = duplicateFileInfo;
        
        try {
            console.log('🗑️ FRONTEND: Deleting existing document:', existingDocument.Id);
            
            // Delete the existing document first
            await axiosInstance.delete(`/api/docuware/coa-certificates/delete/${existingDocument.Id}/`);
            
            console.log('✅ FRONTEND: Existing document deleted successfully');
            
            // Close modal and proceed with upload using original filename
            setShowDuplicateModal(false);
            setDuplicateFileInfo(null);
            
            await performCoaFileUpload(commodityType, file, file.name);
            
        } catch (error) {
            console.error('❌ FRONTEND: Error deleting existing document:', error);
            showError(getErrorMessage(error, 'Commodity operation failed. Please try again.'));
            
            // Close modal even if deletion failed
            setShowDuplicateModal(false);
            setDuplicateFileInfo(null);
        }
    };

    const handleDuplicateCancel = () => {
        console.log('❌ FRONTEND: User cancelled upload due to duplicate');
        
        // Simply close the modal and reset state
        setShowDuplicateModal(false);
        setDuplicateFileInfo(null);
    };

    // Download COA document
    const downloadCoaDocument = async (documentId, filename) => {
        try {
            const response = await axiosInstance.get(
                `/api/docuware/coa-certificates/download/${documentId}/`,
                { responseType: 'blob' }
            );
            
            // Extract filename from headers if available
            let downloadFilename = filename;
            const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    downloadFilename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            
            if (!downloadFilename || downloadFilename === `COA_Document_${documentId}`) {
                const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
                let extension = '';
                if (contentType.includes('pdf')) {
                    extension = '.pdf';
                } else if (contentType.includes('image')) {
                    extension = '.jpg';
                } else if (contentType.includes('document') || contentType.includes('word')) {
                    extension = '.doc';
                }
                downloadFilename = `COA_Document_${documentId}${extension}`;
            }
            
            // Create blob URL and trigger download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = downloadFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            console.log(`Downloaded COA document ${documentId} as "${downloadFilename}"`);
            
        } catch (error) {
            console.error('Error downloading COA document:', error);
            showError(getErrorMessage(error, 'Commodity operation failed. Please try again.'));
        }
    };

    // Delete COA document
    const deleteCoaDocument = async (documentId, filename) => {
        try {
            await axiosInstance.delete(`/api/docuware/coa-certificates/delete/${documentId}/`);
            
            // Refresh document list
            fetchCoaDocuments();
            
            showSuccess(`Document "${filename}" deleted successfully`);
            
        } catch (error) {
            console.error('Error deleting COA document:', error);
            showError(getErrorMessage(error, 'Commodity operation failed. Please try again.'));
        }
    };

    // Extract document metadata for display
    const getCoaDocumentInfo = (document) => {
        const fields = document.Fields || [];
        const getFieldValue = (fieldName) => {
            const field = fields.find(f => f.FieldName === fieldName);
            return field?.Item || '';
        };
        
        const subject = getFieldValue('SUBJECT');
        const originalFilename = subject && subject !== 'COA Certificate' ? subject : null;
        const fallbackFilename = `coa_document_${document.Id}.pdf`;
        const filename = originalFilename || fallbackFilename;
        
        const dateUploaded = getFieldValue('DATE_UPLOADED');
        const commodityType = getFieldValue('COMMODITY_TYPE');
        const employeeId = getFieldValue('EMPLOYEE_ID');
        
        return {
            filename,
            dateUploaded,
            commodityType,
            fileSize: document.FileSize || 0,
            documentId: document.Id,
            employeeId
        };
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        
        try {
            let date;
            
            if (typeof dateString === 'string') {
                // Check for .NET JSON date format
                const netDateMatch = dateString.match(/\/Date\((\d+)\)\//);
                if (netDateMatch) {
                    const timestamp = parseInt(netDateMatch[1], 10);
                    date = new Date(timestamp);
                } else {
                    // Handle regular ISO format dates
                    const cleanDateString = dateString.replace(/Z+$/, 'Z').trim();
                    date = new Date(cleanDateString);
                }
            } else {
                date = new Date(dateString);
            }
            
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (error) {
            console.error('Date formatting error:', error);
            return 'Invalid Date';
        }
    };

    return (
        <div className="crm-tab-panel">
            {/* Traded Commodities Section */}
            <div className="crm-form-section">
                <div className="crm-multi-profile-container">
                    <div className="crm-multi-profile-header">
                        <h2 className="crm-multi-profile-title">Traded Commodities</h2>
                        <button
                            type="button"
                            className="crm-add-profile-btn"
                            onClick={addTradedCommodity}
                        >
                            <span>+</span>
                            Add Commodity
                        </button>
                    </div>

                    {formData.traded_commodities?.length === 0 && (
                        <div className="crm-empty-state">
                            <div className="crm-empty-icon">
                                <CRMIcon name="commodity-trading" size="2x" />
                            </div>
                            <h3>No commodities added yet</h3>
                            <p>Add traded commodities to track your partner's trading portfolio.</p>
                        </div>
                    )}

                    {formData.traded_commodities?.map((commodity, index) => (
                        <div key={commodity.id} className="crm-profile-block">
                            <div className="crm-profile-block-header">
                                <h3 className="crm-profile-block-title">
                                    Commodity {index + 1}
                                    {commodity.commodity_name && (
                                        <span className="crm-contact-name-preview"> - {commodity.commodity_name}</span>
                                    )}
                                </h3>
                                <button
                                    type="button"
                                    className="crm-btn-danger crm-remove-btn"
                                    onClick={() => removeTradedCommodity(commodity.id)}
                                    title="Remove commodity"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="crm-form-grid-2">
                                <div className="crm-input-group">
                                    <label className="crm-input-label required">Commodity Name</label>
                                    <input
                                        type="text"
                                        className="crm-input"
                                        value={commodity.commodity_name}
                                        onChange={(e) => handleCommodityChange(commodity.id, 'commodity_name', e.target.value)}
                                        placeholder="Commodity name"
                                    />
                                </div>
                                <div className="crm-input-group">
                                    <label className="crm-input-label">Specifications</label>
                                    <input
                                        type="text"
                                        className="crm-input"
                                        value={commodity.specifications}
                                        onChange={(e) => handleCommodityChange(commodity.id, 'specifications', e.target.value)}
                                        placeholder="Grade, quality, certifications"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Known Trade Partners Section */}
            <div className="crm-form-section">
                <h2 className="crm-section-title">Other Known Trade Partners</h2>
                
                <div className="crm-input-group">
                    <label className="crm-input-label">CRM Partners This Entity Trades With or Is Connected To</label>
                    
                    <div className="crm-modern-multi-select-container" ref={knownPartnersRef}>
                        <div 
                            className={`crm-modern-multi-select-header ${showKnownPartnersDropdown ? 'active' : ''}`}
                            onClick={() => {
                                setShowKnownPartnersDropdown(!showKnownPartnersDropdown);
                                // Focus search input when opening dropdown
                                if (!showKnownPartnersDropdown) {
                                    setTimeout(() => {
                                        if (knownPartnersSearchInputRef.current) {
                                            knownPartnersSearchInputRef.current.focus();
                                        }
                                    }, 150);
                                }
                            }}
                        >
                            <span>
                                {loadingKnownPartners ? 
                                    'Loading partner names...' :
                                    formData.known_trade_partners?.length === 0 
                                        ? 'Click to search and select CRM partners this entity trades with or is connected to' 
                                        : `${formData.known_trade_partners.length} selected`}
                            </span>
                        </div>
                        
                        {showKnownPartnersDropdown && !loadingKnownPartners && (
                            <div className="crm-modern-multi-select-dropdown">
                                {/* Search Input */}
                                <div className="crm-search-input-container">
                                    <input
                                        ref={knownPartnersSearchInputRef}
                                        type="text"
                                        className="crm-search-input"
                                        placeholder="Type to search partner names..."
                                        value={knownPartnersSearchTerm}
                                        onChange={(e) => setKnownPartnersSearchTerm(e.target.value)}
                                        autoFocus
                                    />
                                </div>

                                {/* Only show action buttons when there are search results */}
                                {filteredKnownPartnersOptions.length > 0 && (
                                    <div className="crm-modern-multi-select-actions">
                                        <button 
                                            type="button" 
                                            className="crm-modern-multi-select-action-btn"
                                            onClick={() => {
                                                const currentSelections = formData.known_trade_partners || [];
                                                const newSelections = [...new Set([...currentSelections, ...filteredKnownPartnersOptions])];
                                                handleInputChange('known_trade_partners', newSelections);
                                            }}
                                        >
                                            Select All Shown
                                        </button>
                                        <button 
                                            type="button" 
                                            className="crm-modern-multi-select-action-btn"
                                            onClick={() => {
                                                handleInputChange('known_trade_partners', []);
                                            }}
                                        >
                                            Deselect All
                                        </button>
                                    </div>
                                )}
                                
                                <div className="crm-modern-multi-select-options">
                                    {!debouncedKnownPartnersSearchTerm.trim() ? (
                                        <div className="crm-modern-multi-select-option">
                                            <span style={{ fontStyle: 'italic', color: '#666' }}>
                                                Start typing to search for CRM partners...
                                            </span>
                                        </div>
                                    ) : filteredKnownPartnersOptions.length > 0 ? (
                                        filteredKnownPartnersOptions.map((partnerName) => (
                                            <div key={partnerName} className="crm-modern-multi-select-option">
                                                <label className="crm-modern-checkbox-container">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.known_trade_partners?.includes(partnerName) || false}
                                                        onChange={() => handleKnownPartnersToggle(partnerName)}
                                                    />
                                                    <span className="crm-modern-checkbox-checkmark"></span>
                                                    {partnerName}
                                                </label>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="crm-modern-multi-select-option">
                                            <span style={{ fontStyle: 'italic', color: '#666' }}>
                                                No partners found matching "{debouncedKnownPartnersSearchTerm}"
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Selected Known Trade Partners Tags */}
                    {formData.known_trade_partners?.length > 0 && (
                        <div className="crm-selected-tags">
                            {formData.known_trade_partners.map((tag, index) => {
                                // Extract partner name if tag is an object, otherwise use as string
                                const partnerName = typeof tag === 'object' && tag !== null 
                                    ? (tag.trade_partner_name || tag.partner_name || '') 
                                    : tag;
                                    
                                return (
                                    <div key={index} className="crm-tag">
                                        {partnerName}
                                        <button
                                            type="button"
                                            className="crm-tag-remove"
                                            onClick={() => removeKnownPartnerTag(partnerName)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}


                </div>
            </div>

            {/* Certificates COA Section - Enhanced with DocuWare Integration */}
            <div className="crm-form-section">
                <h2 className="crm-section-title">Certificates COA Commodity Specifications</h2>
                
                {/* Control Buttons */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '30px' 
                }}>
                    <div>
                        <label className="crm-input-label">COA Certificate Documents (auto-loaded)</label>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            Documents are automatically loaded and grouped by commodity type
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            type="button"
                            onClick={() => setShowCoaDocuments(!showCoaDocuments)}
                            style={{
                                background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)'
                            }}
                        >
                            {showCoaDocuments ? 'Hide Documents' : 'View Documents'}
                        </button>
                        {showCoaDocuments && (
                            <button
                                type="button"
                                onClick={fetchCoaDocuments}
                                disabled={loadingCoaDocuments}
                                style={{
                                    background: loadingCoaDocuments ? '#6c757d' : 'linear-gradient(135deg, #5a7b35, #4a6b25)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: loadingCoaDocuments ? 'not-allowed' : 'pointer',
                                    opacity: loadingCoaDocuments ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                    boxShadow: loadingCoaDocuments ? 'none' : '0 2px 4px rgba(74, 107, 37, 0.15)'
                                }}
                            >
                                {loadingCoaDocuments ? 'Refreshing...' : '↻ Refresh'}
                            </button>
                        )}
                        {/* Upload button - always visible but disabled when profile not saved */}
                        <div style={{ position: 'relative' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    if (formData.traded_commodities?.length === 0) {
                                        showError('Please add traded commodities first before uploading COA certificates.');
                                        return;
                                    }
                                    setShowUploadModal(true);
                                }}
                                disabled={uploadingCoa || isNewProfile || !profileData?.id}
                                style={{
                                    background: (uploadingCoa || isNewProfile || !profileData?.id) ? '#6c757d' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: (uploadingCoa || isNewProfile || !profileData?.id) ? 'not-allowed' : 'pointer',
                                    opacity: (uploadingCoa || isNewProfile || !profileData?.id) ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                    boxShadow: (uploadingCoa || isNewProfile || !profileData?.id) ? 'none' : '0 2px 4px rgba(245, 158, 11, 0.15)'
                                }}
                            >
                                {uploadingCoa ? 'Uploading...' : 'Upload COA Certificate'}
                            </button>
                            {(isNewProfile || !profileData?.id) && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    right: '0',
                                    marginTop: '0.25rem',
                                    fontSize: '0.75rem',
                                    color: '#6b7280',
                                    fontStyle: 'italic',
                                    wordWrap: 'break-word',
                                    lineHeight: '1.2'
                                }}>
                                    Save profile first to upload documents
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Document Display */}
                {showCoaDocuments && (
                    <div className="coa-documents-container" style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        backgroundColor: '#f9fafb',
                        marginBottom: '30px'
                    }}>
                        {loadingCoaDocuments && (
                            <div style={{ 
                                padding: '2rem', 
                                textAlign: 'center',
                                color: '#6b7280'
                            }}>
                                Loading COA documents...
                            </div>
                        )}

                        {coaDocumentError && (
                            <div style={{ 
                                padding: '1rem',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #ef4444',
                                borderRadius: '0.375rem',
                                color: '#dc2626',
                                margin: '1rem'
                            }}>
                                Error: {coaDocumentError}
                            </div>
                        )}

                        {!loadingCoaDocuments && !coaDocumentError && Object.keys(groupedCoaDocuments).length === 0 && (
                            <div style={{ 
                                padding: '2rem', 
                                textAlign: 'center',
                                color: '#6b7280'
                            }}>
                                No COA documents found for this partner
                            </div>
                        )}

                        {!loadingCoaDocuments && !coaDocumentError && Object.keys(groupedCoaDocuments).map(commodityType => (
                            <CommodityDocumentGroup
                                key={commodityType}
                                commodityType={commodityType}
                                documents={groupedCoaDocuments[commodityType]}
                                onDownload={downloadCoaDocument}
                                onDelete={(documentId, filename) => {
                                    setDocumentToDelete({ id: documentId, filename });
                                    setShowDeleteConfirmation(true);
                                }}
                                getDocumentInfo={getCoaDocumentInfo}
                                formatDate={formatDate}
                                formatFileSize={formatFileSize}
                                getEmployeeInfoByUserId={getEmployeeInfoByUserId}
                            />
                        ))}
                    </div>
                )}
                
                {/* Upload Modal */}
                <CommodityUploadModal
                    isOpen={showUploadModal}
                    onClose={() => setShowUploadModal(false)}
                    tradedCommodities={formData.traded_commodities || []}
                    onCommoditySelect={handleCoaFileUpload}
                    uploading={uploadingCoa}
                />

                {/* Duplicate Detection Modal */}
                {showDuplicateModal && duplicateFileInfo && (
                    <div className="confirmation-popup-overlay">
                        <div className="confirmation-popup">
                            <div className="confirmation-popup-message">
                                <h3 style={{ 
                                    margin: '0 0 1rem 0', 
                                    color: '#374151',
                                    fontSize: '1.25rem',
                                    fontWeight: '600'
                                }}>
                                    Duplicate File Detected
                                </h3>
                                <p style={{ margin: '0 0 1rem 0', color: '#6b7280' }}>
                                    A file with the name "<strong>{duplicateFileInfo.file.name}</strong>" already exists in this partner's COA documents.
                                </p>
                                <p style={{ margin: '0', color: '#374151', fontSize: '0.95rem' }}>
                                    What would you like to do?
                                </p>
                            </div>
                            <div className="confirmation-popup-buttons" style={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                gap: '0.75rem',
                                marginTop: '1.5rem',
                                flexWrap: 'wrap' 
                            }}>
                                <button 
                                    onClick={handleDuplicateKeepBoth}
                                    disabled={uploadingCoa}
                                    style={{
                                        background: uploadingCoa ? '#6c757d' : 'linear-gradient(135deg, #10b981, #059669)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '0.6rem 1rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: uploadingCoa ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        minWidth: '90px'
                                    }}
                                >
                                    Keep Both
                                </button>
                                <button 
                                    onClick={handleDuplicateReplace}
                                    disabled={uploadingCoa}
                                    style={{
                                        background: uploadingCoa ? '#6c757d' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '0.6rem 1rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: uploadingCoa ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        minWidth: '90px'
                                    }}
                                >
                                    Replace
                                </button>
                                <button 
                                    onClick={handleDuplicateCancel}
                                    disabled={uploadingCoa}
                                    style={{
                                        background: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '0.6rem 1rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: uploadingCoa ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        minWidth: '90px'
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Popup */}
                {showDeleteConfirmation && documentToDelete && (
                    <ConfirmationPopup
                        message={`Are you sure you want to delete "${documentToDelete.filename}"? This action cannot be undone.`}
                        onConfirm={() => {
                            deleteCoaDocument(documentToDelete.id, documentToDelete.filename);
                            setShowDeleteConfirmation(false);
                            setDocumentToDelete(null);
                        }}
                        onCancel={() => {
                            setShowDeleteConfirmation(false);
                            setDocumentToDelete(null);
                        }}
                    />
                )}
            </div>

            {/* Trading Stats Section */}
            <div className="crm-form-section">
                <h2 className="crm-section-title">Trading Statistics</h2>
                
                <div className="crm-placeholder-section" style={{
                    textAlign: 'center',
                    padding: '1.5rem 1rem',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    color: '#6c757d'
                }}>
                    <h3 style={{ 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        marginBottom: '0.375rem',
                        color: '#495057'
                    }}>
                        Pending API Integration
                    </h3>
                    <p style={{ 
                        fontSize: '0.875rem', 
                        margin: '0',
                        lineHeight: '1.4'
                    }}>
                        Trading statistics will be available once the Trade API and Accounting API integration is completed.
                    </p>
                </div>
            </div>


        </div>
    );
};

// Component to display individual document with employee info
const DocumentInfoItem = ({ document, onDownload, onDelete, getDocumentInfo, formatDate, formatFileSize, getEmployeeInfoByUserId }) => {
    const [employeeInfo, setEmployeeInfo] = useState(null);
    const [loadingEmployee, setLoadingEmployee] = useState(false);
    
    const docInfo = getDocumentInfo(document);
    
    // Fetch employee info when component mounts or employeeId changes
    useEffect(() => {
        const fetchEmployeeInfo = async () => {
            if (!docInfo.employeeId) return;
            
            setLoadingEmployee(true);
            try {
                const empInfo = await getEmployeeInfoByUserId(docInfo.employeeId);
                setEmployeeInfo(empInfo);
            } catch (error) {
                console.error('Error fetching employee info for document:', error);
            } finally {
                setLoadingEmployee(false);
            }
        };
        
        fetchEmployeeInfo();
    }, [docInfo.employeeId]);
    
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.75rem',
            marginBottom: '0.5rem',
            backgroundColor: '#ffffff',
            borderRadius: '6px',
            border: '1px solid #e5e7eb'
        }}>
            <div style={{ flex: 1 }}>
                <div style={{ 
                    fontWeight: 'bold', 
                    color: '#495057',
                    marginBottom: '0.25rem',
                    fontSize: '0.95rem'
                }}>
                    {docInfo.filename}
                </div>
                <div style={{ 
                    fontSize: '0.85rem', 
                    color: '#6c757d',
                    marginBottom: '0.25rem'
                }}>
                    Uploaded: {formatDate(docInfo.dateUploaded)} | Size: {formatFileSize(docInfo.fileSize)}
                </div>
                
                {/* Employee Information Section */}
                <div style={{ 
                    fontSize: '0.85rem', 
                    color: '#495057',
                    marginTop: '0.5rem',
                    padding: '0.4rem 0.6rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6'
                }}>
                    <strong>Uploaded by:</strong>
                    {loadingEmployee ? (
                        <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>Loading...</span>
                    ) : employeeInfo ? (
                        <div style={{ marginTop: '0.25rem' }}>
                            <div><strong>{employeeInfo.first_name} {employeeInfo.last_name}</strong></div>
                            {employeeInfo.position && (
                                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                    {employeeInfo.position}
                                    {employeeInfo.department && ` • ${employeeInfo.department}`}
                                    {employeeInfo.office && ` • ${employeeInfo.office}`}
                                </div>
                            )}
                            <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                Employee ID: {employeeInfo.employee_id}
                            </div>
                        </div>
                    ) : (
                        <span style={{ marginLeft: '0.5rem', fontStyle: 'italic', color: '#6c757d' }}>
                            Employee info not available
                        </span>
                    )}
                </div>
                
                <div style={{ 
                    fontSize: '0.8rem', 
                    color: '#6c757d',
                    marginTop: '0.25rem'
                }}>
                    Document ID: {docInfo.documentId}
                </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                <button
                    onClick={() => onDownload(docInfo.documentId, docInfo.filename)}
                    style={{
                        background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minWidth: '80px',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)'
                    }}
                >
                    Download
                </button>
                <button
                    onClick={() => onDelete(docInfo.documentId, docInfo.filename)}
                    style={{
                        background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minWidth: '80px',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(220, 38, 38, 0.15)'
                    }}
                >
                    Delete
                </button>
            </div>
        </div>
    );
};

// Commodity Document Group Component for collapsible display
const CommodityDocumentGroup = ({ 
    commodityType, 
    documents, 
    onDownload, 
    onDelete, 
    getDocumentInfo, 
    formatDate, 
    formatFileSize,
    getEmployeeInfoByUserId 
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    
    return (
        <div className="commodity-document-group">
            <div 
                className="commodity-group-header"
                onClick={() => setIsExpanded(!isExpanded)}
                style={{ 
                    cursor: 'pointer',
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                    borderRadius: '6px',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)',
                    transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(74, 107, 37, 0.25)';
                }}
                onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0px)';
                    e.target.style.boxShadow = '0 2px 4px rgba(74, 107, 37, 0.15)';
                }}
            >
                <h4 style={{ margin: 0, color: 'white', fontWeight: '600', fontSize: '1rem' }}>
                    {commodityType} COA Certificates ({documents.length})
                </h4>
                <span style={{ 
                    fontSize: '1rem', 
                    color: 'white',
                    fontWeight: '600',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                }}>
                    ⌄
                </span>
            </div>
            
            {isExpanded && (
                <div className="commodity-group-documents">
                    {documents.map(document => (
                        <DocumentInfoItem
                            key={document.Id}
                            document={document}
                            onDownload={onDownload}
                            onDelete={onDelete}
                            getDocumentInfo={getDocumentInfo}
                            formatDate={formatDate}
                            formatFileSize={formatFileSize}
                            getEmployeeInfoByUserId={getEmployeeInfoByUserId}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default CRMCommodityTradingTab; 